<template>
  <div class="editor-view-container overflow-hidden">
    <FlsaFormSkeletonLoader v-if="pageLoading" />

    <EditorLayout
      class="h-100 w-100"
      :side-by-side-view="false"
    >
      <template
        v-if="!pageLoading"
        #content
      >
        <FlsaReviewContent
          class="w-100"
          :section="activeFlsaSection"
          :readonly="true"
          :submitted-at="submission?.created_at ?? null"
          :initial-data="submission ?? null"
          :is-submitting="false"
          @update:section="activeFlsaSection = $event"
        />
      </template>

      <template
        #left
      >
        <FlsaReviewStepsSidebar
          :section="activeFlsaSection"
          :jurisdiction="submission?.jurisdiction"
          :readonly="true"
          @update:section="activeFlsaSection = $event"
        />
      </template>

      <template #right>
        <FlsaSubmissionHistory
          v-if="submission && (
            flsaSubmissionProfileUlids.jobProfileUlid
            || flsaSubmissionProfileUlids.draftJobProfileUlid
          )"
          :flsa-form-ulid="submission.flsa_form_ulid"
          :job-profile-ulid="flsaSubmissionProfileUlids.jobProfileUlid"
          :draft-job-profile-ulid="flsaSubmissionProfileUlids.draftJobProfileUlid"
          :current-submission-ulid="submissionUlid"
        />
      </template>
    </EditorLayout>
  </div>
</template>

<script setup>
import {
  computed,
  onMounted,
  ref,
  watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useAppLoading } from '@/stores/app/loading';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { getFlsaSubmission } from '@/api/jobProfile';
import { getFlsaSubmissionProfileUlids } from '@/helpers/flsa';
import { useFlsaSection } from '@/composables/useFlsaSection';
import FlsaFormSkeletonLoader from '@/components/shared/SkeletonLoaders/editor/FlsaFormSkeletonLoader.vue';
import EditorLayout from '@/components/editor/EditorLayout.vue';
import FlsaReviewContent from '@/components/jobProfiles/editor/FlsaReviewContent.vue';
import FlsaReviewStepsSidebar from '@/components/jobProfiles/editor/FlsaReviewStepsSidebar.vue';
import FlsaSubmissionHistory from '@/components/jobProfiles/editor/FlsaSubmissionHistory.vue';

const props = defineProps({
  submissionUlid: {
    type: String,
    default: '',
  },
});

const { pageLoading } = storeToRefs(useAppLoading());
const { setLoading } = useAppLoading();

const { resetJobProfile, loadJobProfile } = useCurrentJobProfile();

const {
  jobProfile,
  isHrisProfile,
} = storeToRefs(useCurrentJobProfile());

const { activeFlsaSection } = useFlsaSection();

const submission = ref(null);

const flsaSubmissionProfileUlids = computed(() => getFlsaSubmissionProfileUlids({
  jobProfile: jobProfile.value,
  isHrisProfile: isHrisProfile.value,
}));

const load = async () => {
  resetJobProfile();

  submission.value = null;

  if (!props.submissionUlid) return;

  try {
    setLoading({ pageLoading: true });

    try {
      const data = await getFlsaSubmission({ ulid: props.submissionUlid });

      submission.value = data;

      const profileUlid = data.job_profile_ulid ?? data.draft_job_profile_ulid;
      const isHris = Boolean(data.job_profile_ulid);

      if (profileUlid) {
        await loadJobProfile({ ulid: profileUlid, isHris, rescore: false });
      }
    } catch (error) {
      useAppSnackbar().addErrorSnackbar({ text: `Failed to load submission — ${error.message}` });
      throw error;
    }
  } finally {
    setLoading({ pageLoading: false });
  }
};

onMounted(async () => {
  await load();
});

watch(() => props.submissionUlid, () => {
  load();
});
</script>

<style scoped lang="less">
.editor-view-container {
  :deep(.editor-layout > .content) {
    height: calc(100vh - 60px);
  }

  :deep(.content-footer) {
    display: none;
  }
}
</style>
